import avatarPlaceholderUrl from 'images/avatar_placeholder.png'
import type {
  InboxConversationDetail,
  InboxMessage,
  InboxParticipant
} from './inboxTypes'
import type { Review } from 'components/supplier_show/prop_shapes/review'

// Backend provides a default thumbnail; keep this as a frontend fallback.
export const DEFAULT_AVATAR_URL = avatarPlaceholderUrl

export const truncate = (text: string, max = 50): string => {
  if (text == null) return ''
  return text.length <= max ? text : `${text.slice(0, max)}…`
}

export const displayAuthorName = (
  authorName: string,
  isCurrentUser = false
): string => (isCurrentUser ? 'You' : authorName)

export const joinParticipantNames = (participants: InboxParticipant[]): string => {
  const showFullNames = participants.length === 1
  return participants
    .map((participant) => {
      if (showFullNames) return participant.fullName
      return participant.firstName !== '' ? participant.firstName : participant.fullName
    })
    .filter((name) => name !== '')
    .join(', ')
}

const toTitleCase = (name: string): string =>
  name
    .split(/\s+/)
    .filter((part) => part !== '')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ')

export const formatReviewerRecipientName = (
  authorName: string,
  isAnonymous: boolean
): string => {
  if (isAnonymous) return authorName

  return toTitleCase(authorName)
}

export const toValidDate = (iso: string): Date | null => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

const startOfDayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export interface DayGroup {
  dayKey: string
  label: string
  messages: InboxMessage[]
}

export const groupMessagesByDay = (messages: InboxMessage[]): DayGroup[] => {
  const groups = new Map<string, { time: number, messages: InboxMessage[] }>()

  for (const m of messages) {
    const d = toValidDate(m.createdAt)
    if (d == null) continue

    const key = startOfDayKey(d)
    const existing = groups.get(key) ?? { time: d.getTime(), messages: [] }
    existing.messages.push(m)
    groups.set(key, existing)
  }

  const now = new Date()
  const todayKey = startOfDayKey(now)
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayKey = startOfDayKey(yesterday)

  return Array.from(groups.entries())
    .sort((a, b) => a[1].time - b[1].time) // chronological by day
    .map(([dayKey, group]) => {
      // messages chronological within day
      const sortedMessages = [...group.messages].sort((a, b) => {
        const aTime = toValidDate(a.createdAt)?.getTime() ?? 0
        const bTime = toValidDate(b.createdAt)?.getTime() ?? 0
        return aTime - bTime
      })

      let label: string

      if (dayKey === todayKey) {
        label = 'Today'
      } else if (dayKey === yesterdayKey) {
        label = 'Yesterday'
      } else {
        label = formatDayKeyAsLabel(dayKey)
      }

      return { dayKey, label, messages: sortedMessages }
    })
}

const formatDayKeyAsLabel = (dayKey: string): string => {
  // dayKey = YYYY-MM-DD
  const parsed = new Date(`${dayKey}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return dayKey

  const now = new Date()
  const isCurrentYear = parsed.getFullYear() === now.getFullYear()

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(isCurrentYear ? {} : { year: 'numeric' })
  })
}

// Timestamp formatting for individual messages
export const formatInboxTimestamp = (iso: string): string => {
  const d = toValidDate(iso)
  if (d == null) return ''

  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  })
}

// Avatar selection: first participant, otherwise default avatar.
export const getConversationAvatarUrl = (
  participants: InboxParticipant[]
): string => {
  const firstAvatar = participants[0]?.avatarUrl
  return firstAvatar != null && firstAvatar.length > 0 ? firstAvatar : DEFAULT_AVATAR_URL
}

export const handleAvatarError = (
  image: HTMLImageElement
): void => {
  if (image.dataset.fallbackApplied === 'true') return
  image.dataset.fallbackApplied = 'true'
  image.src = DEFAULT_AVATAR_URL
}

export const isReviewAuthorParticipant = (
  participant: InboxParticipant,
  review: Review | null
): boolean => {
  if (review == null) return false

  if (review.is_anonymous === true) {
    return participant.display === 'anonymous'
  }

  return participant.fullName === review.author.name &&
    participant.orgName === (review.author.organization ?? null)
}

const isAnonymousReview = (conversation: InboxConversationDetail): boolean =>
  conversation.review?.is_anonymous === true

export const getDisplayStateForConversation = (
  conversation: InboxConversationDetail
): 'ALL_VISIBLE' | 'RECIPIENT_ANONYMOUS' | 'SENDER_ANONYMOUS' => {
  if (!isAnonymousReview(conversation)) return 'ALL_VISIBLE'

  const hasAnonymousRecipient = conversation.participants.some((participant) => (
    participant.display === 'anonymous'
  ))

  return hasAnonymousRecipient ? 'RECIPIENT_ANONYMOUS' : 'SENDER_ANONYMOUS'
}
